/**
 * Profile hooks (TanStack Query)
 */

import { useMutation } from '@tanstack/react-query';
import { usersApi } from '../api/endpoints/users';
import { errorToast } from '@/hooks/errorToast';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/auth';
import { toUserPatch } from '../api/types';
import type { UpdateProfileRequest } from '../api/types';

/**
 * Update the current user's profile (PUT /me). Each accepted change creates a
 * new version in the user-record event log server-side. On success the
 * canonical user returned by the server is written back to the auth store so
 * every screen reflects the edit immediately.
 *
 * The write is unconditional: the later of two saves wins.
 */
export function useUpdateProfile() {
  const updateUser = useAuthStore((s) => s.updateUser);

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => usersApi.updateMe(data),
    onSuccess: (user) => {
      // toUserPatch, not toUser: PUT /me returns the PROFILE half of a user,
      // so merging a full mapping would write its defaults over the caller
      // capabilities GET /me resolved — emptying the agent launcher and the
      // admin tab strip on a name change. The narrative rides in on this patch,
      // so the profile screen shows what she just wrote without a second read.
      updateUser(toUserPatch(user));
    },
    onError: () => {
      errorToast({
        context: 'updateProfile',
        description: i18n.t('profile.errors.saveFailed', {
          defaultValue: 'Profile could not be saved',
        }),
      });
    },
  });
}
